import { Vector } from "./Vector";

export abstract class Body {
    private panel: HTMLDivElement;
    movable: boolean = true;
    ableToBeMoved: boolean = true;
    touchable: boolean = true;
    grounded: boolean = false;
    bouncy: boolean = false;
    size: Vector = new Vector(1, 1);
    position: Vector = new Vector(0, 0);
    velocity: Vector = new Vector(0, 0);
    actingForces: Vector[] = [];
    constructor() {
        this.panel = document.createElement("div");
        this.panel.classList.add("bodyElement");
        this.panel.style.position = "absolute";
    }

    get width(): number {
        return this.size.get(0);
    }

    get height(): number {
        return this.size.get(1);
    }

    get x(): number {
        return this.position.get(0);
    }

    get y(): number {
        return this.position.get(1);
    }

    get cx(): number {
        return this.centerPosition.get(0);
    }

    get cy(): number {
        return this.centerPosition.get(1);
    }

    get centerPosition(): Vector {
        return this.position.add(this.size.multiple(1 / 2));
    }

    get vertices(): Vector[] {
        return [this.position.copy, this.position.add(new Vector(this.width, 0)), this.position.add(this.size), this.position.add(new Vector(0, this.height))];
    }

    get v(): Vector {
        return this.velocity;
    }

    get a(): Vector {
        let result = new Vector(0, 0);
        this.forces.forEach((f) => {
            result = result.add(f);
        });
        return result;
    }

    get element(): HTMLDivElement {
        return this.panel;
    }

    get forces(): Vector[] {
        return this.actingForces;
    }

    setPosition(position: Vector) {
        this.position = position;
        this.updatePanel();
    }

    setCenterPosition(centerPosition: Vector) {
        this.position = centerPosition.sub(this.size.multiple(1 / 2));
        this.updatePanel();
    }

    setSize(size: Vector) {
        this.size = size;
        this.updatePanel();
    }

    setVelocity(velocity: Vector) {
        this.velocity = velocity;
    }

    move(position: Vector): void {
        this.setPosition(this.position.add(position));
    }

    progressTime() {
        if (!this.movable) {
            return;
        }
        this.setVelocity(this.v.add(this.a));
        this.move(this.velocity);
        this.actingForces = [];
        this.grounded = false;
        this.bouncy = false;
    }

    applyNormalForce(differenceVector: Vector): void {
        if (this.grounded && !this.bouncy) {
            this.forces.push(this.velocity.multiple(-1));
        } else {
            this.forces.push(this.velocity.multiple(-Math.min(differenceVector.size, 20) * 0.05));
        }
    }

    smallSize(size: number): number {
        return size / 2;
    }

    private updatePanel() {
        this.panel.style.width = this.size.get(0) + "px";
        this.panel.style.height = this.size.get(1) + "px";
        this.panel.style.left = this.position.get(0) + "px";
        this.panel.style.top = this.position.get(1) + "px";
    }

    abstract isTouchingWith(body: Body): boolean;

    abstract repels(body: Body): void;
}

export class Square extends Body {
    constructor() {
        super();
        this.element.classList.add("square");
    }

    /**
     * 指定したBodyと接触しているか
     * @param body 判定したいBody
     * @returns 接触しているかどうか
     */
    isTouchingWith(body: Body): boolean {
        if (body instanceof Square) {
            let touching = false;
            this.vertices.forEach((v) => {
                if (body.x <= v.get(0) && v.get(0) <= body.x + body.width) {
                    if (body.y <= v.get(1) && v.get(1) <= body.y + body.height) {
                        touching = true;
                    }
                }
            });
            body.vertices.forEach((v) => {
                if (this.x <= v.get(0) && v.get(0) <= this.x + this.width) {
                    if (this.y <= v.get(1) && v.get(1) <= this.y + this.height) {
                        touching = true;
                    }
                }
            });
            if (touching) {
                return true;
            }
        }
        return false;
    }

    /**
     * 指定したBodyと触れているならば自分のみ反発する
     * @param body 反発する相手のBody
     */
    repels(body: Body): void {
        if (!this.movable || (!body.touchable && this.movable)) {
            return;
        }
        if (body instanceof Square) {
            if (this.isTouchingWith(body)) {
                const differences = [
                    (this.height + body.height) / 2 - (body.cy - this.cy),
                    (this.width + body.width) / 2 - (this.cx - body.cx),
                    (this.height + body.height) / 2 - (this.cy - body.cy),
                    (this.width + body.width) / 2 - (body.cx - this.cx),
                ];
                let index = 0;
                let minDifference = Infinity;
                for (let i = 0; i < 4; i++) {
                    if (differences[i] < minDifference) {
                        minDifference = differences[i];
                        index = i;
                    }
                }
                const difference = differences[index];
                let differenceVector = new Vector(0, 0);
                switch (index) {
                    case 0:
                        differenceVector = new Vector(0, -difference);
                        break;
                    case 1:
                        differenceVector = new Vector(difference, 0);
                        break;
                    case 2:
                        differenceVector = new Vector(0, difference);
                        break;
                    case 3:
                        differenceVector = new Vector(-difference, 0);
                        break;
                }
                if (this.ableToBeMoved) {
                    if (differenceVector.isZero) {
                        return;
                    }
                    if (!body.movable || !body.ableToBeMoved) {
                        this.move(differenceVector);
                    } else {
                        this.move(differenceVector.setSize(this.smallSize(difference)));
                    }
                }
                this.applyNormalForce(differenceVector);
                if (index == 0) {
                    this.grounded = true;
                }
            }
        }
    }
}
